import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { convexQuery } from "@convex-dev/react-query";
import { useState, useMemo } from "react";
import { api } from "@constructionautomation/backend";
import { Loader2 } from "lucide-react";

import { PageHeader } from "@/components/layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { TimeRangeSelect } from "@/components/admin/time-range-select";
import { CostBarChart } from "@/components/admin/cost-bar-chart";
import { CostTrendChart } from "@/components/admin/cost-trend-chart";
import { AppLink } from "@/components/app-link";
import {
  formatCost,
  formatOperationType,
  getTimeRangeBounds,
} from "@/lib/cost";
import type { TimeRange } from "@/lib/cost";

type PlatformOverview = {
  totalCost: number;
  totalOps: number;
  orgBreakdown: Array<{
    orgId: string;
    orgName: string;
    totalCost: number;
    opCount: number;
  }>;
  unattributed?: {
    totalCost: number;
    opCount: number;
  };
};

type TimeSeriesRow = { month: string; totalCost: number; opCount: number };
type CostByTypeRow = { type: string; totalCost: number; opCount: number };

export const Route = createFileRoute("/admin/")({
  component: PlatformOverviewPage,
});

function PlatformOverviewPage() {
  const [timeRange, setTimeRange] = useState<TimeRange>("all");
  const bounds = useMemo(() => getTimeRangeBounds(timeRange), [timeRange]);

  const { data: overviewData } = useQuery(
    convexQuery(api.admin.platform.platformOverview, bounds)
  );
  const { data: timeSeriesData } = useQuery(
    convexQuery(api.admin.platform.platformCostTimeSeries, bounds)
  );
  const { data: costByTypeData } = useQuery(
    convexQuery(api.admin.platform.platformCostByType, bounds)
  );

  const overview = overviewData as PlatformOverview | undefined;
  const timeSeries = timeSeriesData as TimeSeriesRow[] | undefined;
  const costByType = costByTypeData as CostByTypeRow[] | undefined;

  if (overview === undefined || timeSeries === undefined || costByType === undefined) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="size-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const barChartData = overview.orgBreakdown.map((org) => ({
    name: org.orgName,
    rawCost: org.totalCost,
  }));

  return (
    <div className="space-y-6">
      <PageHeader
        title="Cost Dashboard"
        description="Platform-wide cost insights across all organizations"
        actions={
          <TimeRangeSelect value={timeRange} onChange={setTimeRange} />
        }
      />

      {/* Summary stat */}
      <div className="rounded-lg border-2 border-border bg-card px-6 py-4">
        <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
          Total Platform Cost
        </p>
        <p className="mt-1 text-3xl font-bold tracking-tight text-foreground">
          {formatCost(overview.totalCost)}
        </p>
        <p className="text-sm text-muted-foreground">
          {overview.totalOps} completed operation{overview.totalOps !== 1 ? "s" : ""}
        </p>
      </div>

      {/* Charts row */}
      <div className="grid gap-6 lg:grid-cols-2">
        {/* Cost by Organization */}
        <Card className="py-4">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Cost by Organization</CardTitle>
          </CardHeader>
          <CardContent>
            <CostBarChart data={barChartData} height={Math.max(200, barChartData.length * 50)} />
          </CardContent>
        </Card>

        {/* Monthly Trend */}
        <Card className="py-4">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Monthly Cost Trend</CardTitle>
          </CardHeader>
          <CardContent>
            <CostTrendChart data={timeSeries} />
          </CardContent>
        </Card>
      </div>

      {/* Cost by Operation Type */}
      {costByType.length > 0 && (
        <Card className="py-4">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Cost by Operation Type</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="rounded-lg border-2 border-border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="font-bold uppercase tracking-wider text-xs">
                      Type
                    </TableHead>
                    <TableHead className="font-bold uppercase tracking-wider text-xs text-right">
                      Operations
                    </TableHead>
                    <TableHead className="font-bold uppercase tracking-wider text-xs text-right">
                      Total Cost
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {costByType.map((row) => (
                    <TableRow key={row.type}>
                      <TableCell className="font-medium">
                        {formatOperationType(row.type)}
                      </TableCell>
                      <TableCell className="text-right">{row.opCount}</TableCell>
                      <TableCell className="text-right font-semibold">
                        {formatCost(row.totalCost)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Organizations Table */}
      <Card className="py-4">
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Organizations</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="rounded-lg border-2 border-border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="font-bold uppercase tracking-wider text-xs">
                    Organization
                  </TableHead>
                  <TableHead className="font-bold uppercase tracking-wider text-xs text-right">
                    Operations
                  </TableHead>
                  <TableHead className="font-bold uppercase tracking-wider text-xs text-right">
                    Total Cost
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {overview.orgBreakdown.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={3} className="text-center text-muted-foreground py-8">
                      No organization cost data yet
                    </TableCell>
                  </TableRow>
                ) : (
                  overview.orgBreakdown.map((org) => (
                    <TableRow key={org.orgId} className="cursor-pointer hover:bg-muted/50">
                      <TableCell>
                        <AppLink
                          to={`/admin/orgs/${org.orgId}`}
                          className="font-medium text-foreground hover:underline"
                        >
                          {org.orgName}
                        </AppLink>
                      </TableCell>
                      <TableCell className="text-right">{org.opCount}</TableCell>
                      <TableCell className="text-right font-semibold">
                        {formatCost(org.totalCost)}
                      </TableCell>
                    </TableRow>
                  ))
                )}
                {overview.unattributed && (
                  <TableRow className="text-muted-foreground">
                    <TableCell className="font-medium italic">Unattributed</TableCell>
                    <TableCell className="text-right">
                      {overview.unattributed.opCount}
                    </TableCell>
                    <TableCell className="text-right font-semibold">
                      {formatCost(overview.unattributed.totalCost)}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
